import type { Predicate } from '@common/types/objects';
import i18n from '../../../i18n';

import type { CustomUserProperty } from '../types';
import { ECustomUserPropertyType } from '../types';
import {
  isCustomUserPropertyAttribute,
  normalizePredicateBooleanValue,
  parseCustomUserPropertyId,
} from './custom-user-property-predicate';

export function formatCustomUserPropertyPredicateLabel(
  predicate: Predicate,
  properties: CustomUserProperty[] = []
): string | null {
  if (!isCustomUserPropertyAttribute(predicate.attribute)) return null;

  const propertyId = parseCustomUserPropertyId(predicate.attribute);
  const property = properties.find((entry) => String(entry.id) === String(propertyId));
  const fallbackLabel = i18n.t('organisation:import_form_confirm_options_user_property');
  const propertyName = property?.name?.trim()
    || (typeof fallbackLabel === 'string' ? fallbackLabel : '');

  if (property?.type === ECustomUserPropertyType.OPEN_TEXT) {
    return `${propertyName}: ${String(predicate.value ?? '')}`;
  }

  if (property?.type === ECustomUserPropertyType.BOOLEAN) {
    const normalizedValue = normalizePredicateBooleanValue(predicate.value);

    const booleanLabel = normalizedValue === true
      ? i18n.t('common:predicates_filter_user_property_yes')
      : normalizedValue === false
        ? i18n.t('common:predicates_filter_user_property_no')
        : '';
    return booleanLabel
      ? `${propertyName} ${i18n.t('common:predicates_filter_is')} ${booleanLabel}`
      : propertyName;
  }

  if (property?.type === ECustomUserPropertyType.DROPDOWN) {
    const selectedIds = Array.isArray(predicate.value)
      ? predicate.value
      : [predicate.value];
    const labels = selectedIds
      .map((optionId) => property.options?.find((option) => option.id === optionId)?.value)
      .filter((label): label is string => !!label);

    return labels.length > 0
      ? `${propertyName}: ${labels.join(', ')}`
      : propertyName;
  }

  return propertyName;
}

export function getCustomUserPropertyPredicates(
  predicates: Predicate[] = []
): Predicate[] {
  return predicates.filter((predicate) => isCustomUserPropertyAttribute(predicate.attribute));
}
